/* eslint-disable react/no-danger */
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { makeStyles, useTheme, darken } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Drawer,
  AppBar,
  Toolbar,
  CssBaseline,
  IconButton,
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
  Container,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useMediaQuery } from "@mui/material";
import clsx from "clsx";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import MenuIcon from "@material-ui/icons/Menu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometerAlt,
  faIdCard,
  faSignOutAlt,
  faGraduationCap,
  faUser,
  faHome,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet";
import topBg from "../img/appBarBg.png";
import sideBg from "../img/menuBg-min.png";
import logo from "../img/newZims3.png";
import DealerRoutes from "./routes/DealerRoutes";
import NotificationCenter from "./Notifications/NotificationCentre";
import PendingAccount from "./authenticate/PendingAccount";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import {
  getUniversityStatus,
  fetchAllModules,
} from "./shared/functions/getUniversityStatus";

// Defined outside component to avoid re-creation on each render
const DRAWER_WIDTH = 240;

/**
 * Navigation menu items configuration
 */
const navigationConfig = {
  leaderItems: [
    { title: "Dashboard", path: "/", icon: faTachometerAlt },
    { title: "Your Team", path: "/manage-dealers", icon: faIdCard },
    { title: "Zawadi University", path: "/training", icon: faGraduationCap },
    { title: "Profile", path: "/profile", icon: faUser },
    { title: "Alerts", path: "/alerts", icon: faBell },
  ],
  baseItems: [
    { title: "Dashboard", path: "/", icon: faTachometerAlt },
    { title: "Zawadi University", path: "/training", icon: faGraduationCap },
    { title: "Profile", path: "/profile", icon: faUser },
  ],
  waitingStatusItems: [
    { title: "Home", path: "/", icon: faHome },
    { title: "Zawadi University", path: "/training", icon: faGraduationCap },
    { title: "Profile", path: "/profile", icon: faUser },
  ],
  waitingStatusItemsDashboard: [
    { title: "Dashboard", path: "/", icon: faTachometerAlt },
    { title: "Zawadi University", path: "/training", icon: faGraduationCap },
    { title: "Profile", path: "/profile", icon: faUser },
  ],
};

/**
 * DealerMenu Component - Main navigation for the dealer dashboard
 *
 * @returns {React.ReactElement} The DealerMenu component
 */
export default function DealerMenu() {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { logout, currentUser } = useAuth();
  const { activeSuppliers, productTypes } = useCompany();

  // UI State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // User State
  const [currentUserTitle, setCurrentUserTitle] = useState("");
  const [universityStatus, setUniversityStatus] = useState(2);
  const [showWaitingStatus, setShowWaitingStatus] = useState(false);

  // Alerts State
  const [showTrainingAlert, setShowTrainingAlert] = useState(false);

  const classes = useStyles(theme, DRAWER_WIDTH, isMobile);

  // Handlers
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleDrawerClose = () => setDrawerOpen(false);
  const handleLogOut = async () => {
    await logout();
    history.push("/login");
  };

  /**
   * Copy dealer code to clipboard
   */
  const copyUserId = () => {
    const text = currentUser.id ? `${currentUser.id}:` : "";

    const input = document.createElement("textarea");
    input.innerHTML = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);

    // eslint-disable-next-line no-alert
    alert("Name copied.");
  };

  /**
   * Fetch user's university training status
   */
  const updateUniversityStatus = async () => {
    const { data, error } = await fetchAllModules(currentUser.id, true);
    const status = getUniversityStatus(
      currentUser,
      activeSuppliers,
      productTypes,
      data,
      error
    );
    setUniversityStatus(status.status);
  };

  // Effects
  useEffect(() => {
    if (currentUser) {
      setCurrentUserTitle(`${currentUser.first_name} ${currentUser.surname}`);
      updateUniversityStatus();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      if (
        !currentUser.email_verified ||
        !currentUser.phone_verified ||
        currentUser.refs_user_lifecycle_status_id !== 2 ||
        ((universityStatus === 102 || universityStatus < 0) &&
          currentUser.refs_type_id === 1)
      ) {
        setShowWaitingStatus(true);
      } else if (universityStatus === 101 && currentUser.refs_type_id === 1) {
        setShowWaitingStatus(false);
        setShowTrainingAlert(true);
      } else if (universityStatus === 0) {
        setShowWaitingStatus(false);
      }
      setLoading(false);
    }
  }, [currentUser, universityStatus]);

  // Decide which navigation items to show
  const getNavigationItems = () => {
    if (showWaitingStatus) {
      return navigationConfig.waitingStatusItems;
    }

    return currentUser?.is_leader
      ? navigationConfig.leaderItems
      : navigationConfig.baseItems;
  };

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Zawadi</title>
      </Helmet>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        elevation={0}
        position="absolute"
        className={classes.appBar}
        style={{
          background: `url(${topBg})`,
          backgroundColor: "#fff",
          backgroundSize: "cover",
        }}
      >
        <Toolbar>
          <IconButton
            style={{ color: "#fff" }}
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
          >
            <MenuIcon />
          </IconButton>

          <img src={logo} width={130} alt="Logo" />

          <Typography
            variant="h6"
            noWrap
            style={{ flex: 1 }}
            className={classes.menuText}
          />
          {/* Notifications Icon */}
          {!showWaitingStatus && <NotificationCenter />}
        </Toolbar>
      </AppBar>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {/* Navigation Drawer */}
          <Drawer
            className={classes.drawer}
            variant="persistent"
            anchor="left"
            open={drawerOpen}
            classes={{ paper: classes.drawerPaper }}
          >
            <div className={classes.drawerHeader}>
              <div style={{ width: "100%", padding: "10px 10px 0 0" }} />
              <IconButton style={{ color: "#fff" }} onClick={handleDrawerClose}>
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </div>

            {/* User Profile Info */}
            <Box className={classes.userDetails}>
              <Box
                onClick={copyUserId}
                style={{ paddingLeft: "1.25rem", cursor: "pointer" }}
              >
                <Typography variant="subtitle2">
                  {currentUser.id ? `Z-${currentUser.id}: ` : null}
                  {currentUserTitle}
                </Typography>
                <Typography variant="subtitle2" style={{ opacity: 0.5 }}>
                  Click to Copy
                </Typography>
              </Box>
            </Box>

            {/* Navigation Links */}
            <List className={classes.list}>
              {getNavigationItems().map(item => (
                <Link
                  to={item.path}
                  className="drawer-item"
                  key={`${item.title}`}
                  onClick={handleDrawerClose}
                >
                  <ListItem className={classes.hoverColor}>
                    <ListItemIcon>
                      <FontAwesomeIcon
                        className={classes.icons}
                        icon={item.icon}
                      />
                    </ListItemIcon>
                    <ListItemText className={classes.menuText}>
                      {item.title}
                    </ListItemText>
                  </ListItem>
                </Link>
              ))}

              {/* Logout Button */}
              <ListItem
                onClick={handleLogOut}
                style={{ cursor: "pointer" }}
                className={classes.hoverColor}
              >
                <ListItemIcon>
                  <FontAwesomeIcon
                    className={classes.icons}
                    icon={faSignOutAlt}
                  />
                </ListItemIcon>
                <ListItemText className={classes.menuText}>Logout</ListItemText>
              </ListItem>
            </List>
          </Drawer>

          {/* Main Content */}
          <Container maxWidth="lg" style={{ padding: -1 }}>
            <main
              className={clsx(classes.content, {
                drawerOpen,
              })}
            >
              <div className={classes.drawerHeader} />

              {showWaitingStatus && <PendingAccount />}
              {showTrainingAlert && (
                <Alert
                  severity="warning"
                  style={{
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    marginTop: "1rem",
                  }}
                  action={
                    <Button
                      onClick={() => {
                        history.push("/training");
                      }}
                    >
                      Do training
                    </Button>
                  }
                >
                  You have not completed all the training modules. Please
                  complete all the training modules to remove this message.
                </Alert>
              )}
              <DealerRoutes
                showWaitingStatus={showWaitingStatus}
                is_leader={currentUser?.is_leader}
              />
            </main>
          </Container>
        </>
      )}
    </div>
  );
}
/**
 * Renders the Dealer Menu component.
 * @param {object} theme - The current theme object.
 * @param {number} drawerWidth - The width of the drawer.
 * @param {boolean} isMobile - Whether the user is on a mobile device.
 */
const useStyles = (theme, drawerWidth, isMobile) =>
  makeStyles(() => ({
    root: {
      display: "flex",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100vh",
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      "& .MuiDrawer-paper": { borderWidth: 0 },
    },
    drawerPaper: {
      width: isMobile ? "100%" : drawerWidth,
      background: `url(${sideBg})`,
      backgroundSize: "cover",
    },
    drawerHeader: {
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      marginLeft: -drawerWidth,
    },
    contentShift: {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    },
    list: {
      backgroundColor: darken(theme.palette.primary.main, 0.2),
    },
    userDetails: {
      display: "flex",
      color: "#fff",
      alignItems: "center",
      padding: "1rem 1.25rem",
      backgroundColor: theme.palette.primary.main,
    },
    detailsAvatar: {
      display: "flex",
      width: "2rem",
      height: "2rem",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "100%",
      backgroundColor: darken(theme.palette.primary.main, 0.2),
    },
    icons: {
      color: "#fff",
      marginLeft: theme.spacing(2),
    },
    menuText: {
      color: "#fff",
      textDecoration: "none",
    },
    hoverColor: {
      "&:hover": {
        background: "rgba(255, 255, 255, .05)",
      },
    },
    notificationIcon: {
      padding: 5,
      marginRight: 25,
      cursor: "pointer",
      background: "#fff",
      borderRadius: 50,
      boxShadow: "0px 0px 12px black",
    },
    alertImageContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 10,
    },
    alertActions: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
  }))();
